import AccessTime from '@mui/icons-material/AccessTime'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Button, CircularProgress, Menu, MenuItem, Stack, Typography } from '@mui/material'

import { isEqual } from 'date-fns/isEqual'
import { parseISO } from 'date-fns/parseISO'
import type { MouseEvent } from 'react'
import { useState } from 'react'

import { formatTimeslotTime } from './timeslotDates'

interface TimeslotSelectionGraphqlProps {
  timeslots: { id: string; startTime: string; endTime: string }[]
  selectedTimeslot: string
  timezone?: string | null
  onChangeTimeslot: (timeslot: string) => void
  disabled?: boolean
}

export function TimeslotSelectionGraphql({
  timeslots: unsortedTimeslots,
  selectedTimeslot,
  timezone,
  onChangeTimeslot,
  disabled = false,
}: TimeslotSelectionGraphqlProps) {
  const timeslots = [...unsortedTimeslots].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleTimeslotSelection = (value: string) => {
    onChangeTimeslot(value)
    handleClose()
  }

  const open = Boolean(anchorEl)

  const selectedDate = parseISO(selectedTimeslot)
  const selectedTimeslotOption = timeslots.find((timeslot) =>
    isEqual(parseISO(timeslot.startTime), selectedDate),
  )

  // Simplified display-only mode when disabled
  if (disabled) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <AccessTime color="action" />
        <Typography variant="body2">
          {selectedTimeslotOption ? (
            <>
              {formatTimeslotTime(selectedTimeslotOption.startTime, timezone)} -{' '}
              {formatTimeslotTime(selectedTimeslotOption.endTime, timezone)}
            </>
          ) : (
            <CircularProgress size={15} />
          )}
        </Typography>
      </Stack>
    )
  }

  return (
    <>
      <Button
        disabled={timeslots.length <= 1}
        onClick={handleClick}
        aria-haspopup="true"
        id="basic-button"
        variant="outlined"
        size="medium"
        fullWidth
        sx={{
          borderColor: 'divider',
          minHeight: '40px',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
        startIcon={<AccessTime color="primary" />}
        endIcon={
          timeslots.length <= 1 ? null : (
            <>{open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}</>
          )
        }>
        <Typography noWrap>
          {selectedTimeslotOption ? (
            <>
              {formatTimeslotTime(selectedTimeslotOption.startTime, timezone)} -{' '}
              {formatTimeslotTime(selectedTimeslotOption.endTime, timezone)}
            </>
          ) : (
            <CircularProgress size={15} />
          )}
        </Typography>
      </Button>
      <Menu
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          maxHeight: '400px',
        }}
        anchorEl={anchorEl}
        id="basic-menu"
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}>
        <Stack width={170}>
          {timeslots.map((timeslot) => (
            <MenuItem
              onClick={() => handleTimeslotSelection(timeslot.startTime)}
              key={`${timeslot.startTime}-${timeslot.endTime}`}>
              <Typography paddingX={2}>
                {formatTimeslotTime(timeslot.startTime, timezone)} -{' '}
                {formatTimeslotTime(timeslot.endTime, timezone)}
              </Typography>
            </MenuItem>
          ))}
        </Stack>
      </Menu>
    </>
  )
}
